"use client";

import { useState } from "react";
import type { ExtensionImageProps } from "./types";
import { PortalModal } from "@/components/ui/portal-modal";
import { cn } from "@/lib/utils";
import { CheckSquare, ZoomIn } from "lucide-react";
import Image from "next/image";

export const ExtensionImage = ({ path, alt, icon, canZoom, iconColor, width }: ExtensionImageProps) => {
  const [isZoom, setIsZoom] = useState(false);

  const fullPath = path ? (path.startsWith("/") ? path : `/${path}`) : undefined;

  const onZoom = () => {
    setIsZoom(true);
  };

  if (!fullPath) return null;

  return (
    <>
      <div className="relative w-full">
        {icon && <CheckSquare className="absolute right-2 bottom-2" style={{ color: iconColor }} />}
        {canZoom && (
          <button
            type="button"
            aria-label="Zoom image"
            className="absolute right-[1px] bottom-3 cursor-pointer focus-visible:ring-2 focus-visible:ring-cede-primary rounded-sm"
            onClick={onZoom}
          >
            <ZoomIn className="text-cede-primary" />
          </button>
        )}
        {canZoom ? (
          <button
            type="button"
            aria-label="Open zoomed image"
            className="my-2 focus-visible:ring-2 focus-visible:ring-cede-primary rounded-sm"
            onClick={onZoom}
          >
            <img
              src={fullPath}
              width={width ?? "100%"}
              alt={alt}
              className={cn("rounded-lg max-h-[50px] cursor-pointer")}
            />
          </button>
        ) : (
          <img
            src={fullPath}
            width={width ?? "100%"}
            alt={alt}
            className={cn("my-2 rounded-lg max-h-none cursor-default")}
          />
        )}
      </div>

      {canZoom && (
        <PortalModal
          open={isZoom}
          onOpenChange={setIsZoom}
          showCloseButton={true}
          className="max-w-[90vw] py-0 z-modal-high"
        >
          {/* Scrollable content area */}
          <PortalModal.Content scrollable className="pt-[84px] pb-6 block overflow-x-auto">
            <Image
              src={fullPath}
              alt={alt ?? ""}
              width={0}
              height={0}
              sizes="100vw"
              style={{ width: "auto", height: "auto" }}
              className="max-w-none rounded-medium"
              unoptimized
            />
          </PortalModal.Content>
        </PortalModal>
      )}
    </>
  );
};
